/**
 * Rango Quantum — estadísticas del dashboard (0.C del estudio: señales
 * totales, %TP, %SL/liquidación, winrate, expectancy en % margen, tabla de
 * últimas N señales). Ventana: últimas 50 señales cerradas dentro de los
 * últimos 90 días **contados por fecha de CIERRE** (`outcomeTime`), no de
 * entrada — ver el comentario dentro de `computeStats` (V3, 2026-08-15).
 *
 * Decisión de producto (Marc, 2026-08-14, validación en pantalla — ver
 * docs/indicators/rango_quantum.md sección 5): las métricas (winrate, %TP1,
 * %TP2, %liquidación, %SL, expectancy) cuentan SOLO señales CERRADAS
 * (outcome !== 'active'). La señal abierta vive en su propia sección
 * ("Señal activa") y NUNCA se mezcla en los porcentajes — antes el
 * encabezado ("90d / N señales") usaba el total (abiertas+cerradas) y el
 * cuerpo usaba solo cerradas, produciendo dos números distintos para lo que
 * el usuario lee como "lo mismo" (bug real encontrado en la validación:
 * encabezado decía 50, cuerpo decía 20). Ahora encabezado y cuerpo se
 * refieren AMBOS a `closedCount`; `openCount` se expone aparte, informativo,
 * nunca como denominador de un porcentaje.
 *
 * Expectancy en % margen: replica el modelo del estudio (margen fijo, PnL
 * en múltiplos de ese margen vía apalancamiento) — ver
 * backend/research/rango_quantum/metrics.py para la definición de
 * referencia. Aquí es una estimación de PRESENTACIÓN (no lleva comisiones/
 * funding, que sí computa el motor Python) — el dashboard lo indica.
 */
import { BASKET_SYMBOLS, VALIDATED_TIMEFRAME } from './constants.js'

const SIGNAL_WINDOW_DAYS = 90
const SIGNAL_WINDOW_COUNT = 50

function pnlPctMargin(sig, leverage) {
  const dirn = sig.direction === 'long' ? 1 : -1
  let exitPrice
  if (sig.outcome === 'liquidated' || sig.outcome === 'sl') {
    exitPrice = sig.outcome === 'liquidated' ? sig.liqPrice : sig.slPrice
  } else if (sig.outcome === 'tp2') {
    // reparto 60/40 (o el configurado) ponderado entre TP1 y TP2.
    exitPrice = sig.tp2
  } else if (sig.outcome === 'time_stop') {
    // Cierre a mercado al close de la vela que dispara el time-stop — el
    // PnL depende de dónde estaba el precio en ese momento, no de un nivel
    // fijo (a diferencia de TP/liq/SL). Puede ser ganador o perdedor.
    exitPrice = sig.timeStopExitPrice
  } else {
    return 0
  }
  const priceMovePct = ((exitPrice - sig.entryPrice) / sig.entryPrice) * dirn
  if (sig.outcome === 'liquidated') return -100 // 100% del margen, por definición del modelo
  return priceMovePct * leverage * 100
}

/**
 * Aviso de contexto honesto (nunca oculta números, los acompaña). Dos
 * condiciones independientes, pueden darse ambas a la vez:
 * - `symbol` fuera de BASKET_SYMBOLS: el estudio (Fase 0/0.5) no lo validó.
 * - `timeframe` distinto de VALIDATED_TIMEFRAME ('4h'): el motor de chart
 *   calcula igual en cualquier TF (mismo código), pero solo 4H tiene
 *   validación out-of-sample Y es el único que el motor de backend evalúa
 *   de verdad para señales reales.
 */
export function buildRecommendation(symbol, timeframe) {
  const notices = []
  const canonicalSymbol = typeof symbol === 'string' ? symbol.toUpperCase().replace(/\//g, '') : ''
  if (timeframe && timeframe !== VALIDATED_TIMEFRAME) {
    notices.push(`Timeframe no validado — config recomendada: ${VALIDATED_TIMEFRAME.toUpperCase()} de serie.`)
  }
  if (canonicalSymbol && !BASKET_SYMBOLS.includes(canonicalSymbol)) {
    notices.push('Activo no incluido en el estudio — estadísticas sin validación OOS.')
  }
  return notices
}

function toSeconds(t) {
  if (typeof t !== 'number' || Number.isNaN(t)) return null
  return t > 1e10 ? t / 1000 : t
}

export function computeStats(signals, cfg, symbol, timeframe) {
  const list = Array.isArray(signals) ? signals : []
  const recommendation = buildRecommendation(symbol, timeframe)
  const cutoffTime = Date.now() / 1000 - SIGNAL_WINDOW_DAYS * 24 * 3600

  // V3 (validación en pantalla de Marc, 2026-08-15): la ventana de 90d
  // cuenta las señales CERRADAS por su FECHA DE CIERRE (`outcomeTime`), no
  // por fecha de entrada. Caso real que destapó el bug: un LONG que abrió
  // fuera de la ventana pero liquidó dentro de ella — contando por entrada,
  // esa liquidación desaparecía por completo de las estadísticas pese a
  // haber ocurrido dentro del periodo que el usuario está mirando. Una
  // señal ABIERTA, por definición, sigue viva ahora mismo — siempre solapa
  // cualquier ventana que incluya el presente, así que se muestra
  // independientemente de cuándo entró (no tiene fecha de cierre con la que
  // filtrar).
  const openAll = list.filter((s) => s.outcome === 'active')
  const closed = list
    .filter((s) => s.outcome !== 'active')
    .filter((s) => {
      const t = toSeconds(s.outcomeTime)
      return t != null && t >= cutoffTime
    })
    .sort((a, b) => toSeconds(a.outcomeTime) - toSeconds(b.outcomeTime))
    .slice(-SIGNAL_WINDOW_COUNT)

  const openCount = openAll.length
  const closedCount = closed.length

  if (closedCount === 0) {
    return {
      closedCount: 0, openCount,
      tp1Rate: 0, tp2Rate: 0, liquidationRate: 0, slRate: 0, timeStopRate: 0,
      winrate: 0, expectancyPctMargin: 0, last10: [], recommendation,
      windowStart: cutoffTime,
    }
  }

  const leverage = cfg?.leverage ?? 5
  const tp1Count = closed.filter((s) => s.tp1Hit).length
  const tp2Count = closed.filter((s) => s.outcome === 'tp2').length
  const liqCount = closed.filter((s) => s.outcome === 'liquidated').length
  const slCount = closed.filter((s) => s.outcome === 'sl').length
  const timeStopCount = closed.filter((s) => s.outcome === 'time_stop').length
  // Win: TP2, o tocó TP1 y no acabó en liquidación (reparto TP1 cuenta como
  // parte ganadora aunque el resto acabe en SL/time-stop) — igual que
  // antes. Time-stop SIN TP1 tocado no tiene un nivel fijo que lo defina
  // ganador/perdedor como TP/liq/SL: se decide por el PnL real de esa
  // operación (precio de cierre a mercado vs entrada).
  const winCount = closed.filter((s) => {
    if (s.outcome === 'tp2') return true
    if (s.tp1Hit && s.outcome !== 'liquidated') return true
    if (s.outcome === 'time_stop') return pnlPctMargin(s, leverage) > 0
    return false
  }).length

  const pnlSum = closed.reduce((sum, s) => sum + pnlPctMargin(s, leverage), 0)

  return {
    closedCount,
    openCount,
    tp1Rate: tp1Count / closedCount,
    tp2Rate: tp2Count / closedCount,
    liquidationRate: liqCount / closedCount,
    slRate: slCount / closedCount,
    timeStopRate: timeStopCount / closedCount,
    winrate: winCount / closedCount,
    expectancyPctMargin: pnlSum / closedCount,
    // Tabla "últimas señales": solo cerradas — la abierta ya vive en su
    // propia sección (Señal activa) y no se duplica aquí.
    last10: closed.slice(-10),
    recommendation,
    windowStart: cutoffTime,
  }
}
